<template>
  <div>
    <div class="row">
      <div class="col">
        <h5 style="margin-bottom: 30px">
          <span>{{ $t("teacherGradePermissions.title") }}</span>
          <span class="mr-2 ml-2">–</span>
          <small class="text-secondary">{{
            $t("teacherGradePermissions.subTitle")
          }}</small>
        </h5>
      </div>
    </div>

    <!-- Trimester Selector -->
    <div class="row mb-4">
      <div class="col-xl-12">
        <div class="d-flex align-items-center">
          <label class="mb-0 mr-3">
            {{ $t("teacherGradePermissions.chooseTrimester") }}:
          </label>
          <a-radio-group
            v-model="selectedTrimester"
            button-style="solid"
            @change="handleTrimesterChange"
          >
            <a-radio-button value="1"
              >{{ $t("emploi.trimestre") }} 1</a-radio-button
            >
            <a-radio-button value="2"
              >{{ $t("emploi.trimestre") }} 2</a-radio-button
            >
            <a-radio-button value="3"
              >{{ $t("emploi.trimestre") }} 3</a-radio-button
            >
          </a-radio-group>
        </div>
      </div>
    </div>

    <!-- Current Trimester Permissions Section -->
    <div class="row">
      <div class="col-xl-12">
        <div class="card">
          <div class="card-body">
            <div class="loading-container" v-if="permissionsLoading">
              <a-spin size="large" />
            </div>
            <div v-else>
              <div class="row mb-4">
                <div class="col-md-12">
                  <div class="d-flex align-items-center mb-4">
                    <h6 class="mb-0 mr-3">
                      {{ $t("teacherGradePermissions.gradeEditing") }}:
                    </h6>
                    <a-switch
                      :checked="trimesterPermissions[selectedTrimester].enabled"
                      @change="
                        (checked) =>
                          toggleTrimesterPermission(selectedTrimester, checked)
                      "
                    />
                    <span
                      class="ml-2"
                      :class="
                        trimesterPermissions[selectedTrimester].enabled
                          ? 'text-success'
                          : 'text-danger'
                      "
                    >
                      {{
                        trimesterPermissions[selectedTrimester].enabled
                          ? $t("teacherGradePermissions.gradeEditingEnabled")
                          : $t("teacherGradePermissions.gradeEditingDisabled")
                      }}
                    </span>
                  </div>

                  <div v-if="trimesterPermissions[selectedTrimester].enabled">
                    <div class="mb-4">
                      <h6 class="mb-3">
                        {{ $t("teacherGradePermissions.teacherAccessMode") }}:
                      </h6>
                      <a-radio-group
                        v-model="
                          trimesterPermissions[selectedTrimester].teacherMode
                        "
                      >
                        <a-radio :value="'all'">
                          {{ $t("teacherGradePermissions.allTeachers") }}
                        </a-radio>
                        <a-radio :value="'specific'">
                          {{ $t("teacherGradePermissions.specificTeachers") }}
                        </a-radio>
                      </a-radio-group>
                    </div>

                    <div
                      v-if="
                        trimesterPermissions[selectedTrimester].teacherMode ===
                        'specific'
                      "
                      class="mb-4"
                    >
                      <div class="row">
                        <div class="col-md-12 mb-1">
                          <h6>
                            {{ $t("teacherGradePermissions.selectTeachers") }}:
                          </h6>
                        </div>
                      </div>

                      <!-- Teacher Filters -->
                      <div class="row mb-4">
                        <div class="col-md-4">
                          <a-input-search
                            v-model="searchText"
                            :placeholder="
                              $t('teacherGradePermissions.searchTeachers')
                            "
                            style="width: 100%"
                            allowClear
                            @search="applyFilters"
                            @change="applyFilters"
                          />
                        </div>
                        <div class="col-md-4">
                          <a-select
                            v-model="selectedClass"
                            style="width: 100%"
                            :placeholder="`${$t('all.filterBy')} ${$t(
                              'profil.classe'
                            )}`"
                            allowClear
                            @change="applyFilters"
                          >
                            <a-select-option value="all"> --- </a-select-option>
                            <a-select-option
                              v-for="classe in classes"
                              :key="classe._id"
                              :value="classe._id"
                            >
                              {{ classe.name }}
                            </a-select-option>
                          </a-select>
                        </div>
                        <div class="col-md-4">
                          <a-select
                            v-model="selectedStatus"
                            style="width: 100%"
                            :placeholder="`${$t(
                              'teacherGradePermissions.filterAccess'
                            )}`"
                            allowClear
                            @change="applyFilters"
                          >
                            <a-select-option value="enabled">
                              {{ $t("teacherGradePermissions.withAccess") }}
                            </a-select-option>
                            <a-select-option value="disabled">
                              {{ $t("teacherGradePermissions.withoutAccess") }}
                            </a-select-option>
                          </a-select>
                        </div>
                      </div>

                      <!-- Teacher Selection Table -->
                      <a-table
                        :columns="teacherColumns"
                        :dataSource="teachers"
                        :loading="tableLoading"
                        :pagination="{ pageSize: 10 }"
                        rowKey="_id"
                      >
                        <template slot="teacherName" slot-scope="text, record">
                          <a-avatar
                            size="large"
                            v-if="record.photo"
                            :src="settings.resourcesUrl.replace(
                              'FILE_PATH',
                              record.photo
                            )"
                          />
                          <a-avatar
                            size="large"
                            style="color: #f56a00; background-color: #fde3cf"
                            v-else
                            >{{ record.fullName[0].toUpperCase() }}</a-avatar
                          >
                          <span class="ml-2">
                            {{ record.fullName }}
                          </span>
                        </template>

                        <template slot="class" slot-scope="text, record">
                          <a-tag
                            color="blue"
                            v-for="item in record.access"
                            :key="item._id"
                          >
                            {{ item.classeRoom.name }}
                          </a-tag>
                        </template>

                        <template slot="subjects" slot-scope="text, record">
                          <div class="subjects-container">
                            <a-tag
                              color="green"
                              v-for="subject in getUniqueSubjects(record)"
                              :key="subject._id"
                            >
                              {{ subject.name }}
                            </a-tag>
                          </div>
                        </template>

                        <template slot="permission" slot-scope="text, record">
                          <a-switch
                            :checked="
                              checkSpecificTrimesterPermission(
                                record._id,
                                selectedTrimester
                              )
                            "
                            @change="
                              (checked) => {
                                toggleSpecificTrimesterPermission(
                                  record,
                                  selectedTrimester,
                                  checked
                                );
                              }
                            "
                          />
                        </template>
                      </a-table>
                    </div>
                  </div>
                </div>
              </div>

              <div class="row mt-4">
                <div class="col-md-12 text-right">
                  <a-button
                    type="primary"
                    @click="savePermissions"
                    :loading="savingPermissions"
                  >
                    {{ $t("action.enregistrer") }}
                  </a-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/*eslint-disable */
import apiClient from "@/services/axios";
import { mapState } from "vuex";

export default {
  props: {
    type: {
      type: String,
      required: true,
    },
  },
  async created() {
    await this.initializeTrimesterPermissions();
    await this.fetchTeachers();
    await this.getClasses();
  },
  data() {
    return {
      selectedTrimester: "1",
      trimesters: ["1", "2", "3"],
      gradeEditingEnabled: false,
      teacherAccessMode: "all", // 'all' or 'specific'
      teachersData: [],
      trimesterPermissions: {},
      tableLoading: false,
      permissionsLoading: false,
      searchText: "",
      savingPermissions: false,
      teachers: [], // Will store fetched teachers
      allTeachers: [], // Will store all fetched teachers for filtering
      selectedClass: undefined,
      selectedStatus: undefined,
      classes: [],
    };
  },
  computed: {
    ...mapState(["settings"]),
    teacherColumns() {
      return [
        {
          title: this.$t("emploi.enseignant"),
          dataIndex: "teacherName",
          key: "teacherName",
          scopedSlots: {
            customRender: "teacherName",
          },
        },
        {
          title: this.$t("accueil.classes"),
          dataIndex: "class",
          key: "class",
          scopedSlots: {
            customRender: "class",
          },
        },
        {
          title: this.$t("bulletin.matieres"),
          dataIndex: "subjects",
          key: "subjects",
          width: 500,
          scopedSlots: {
            customRender: "subjects",
          },
        },
        {
          title: this.$t("teacherGradePermissions.canEdit"),
          dataIndex: "permission",
          key: "permission",
          scopedSlots: {
            customRender: "permission",
          },
        },
      ];
    },
  },
  methods: {
    async initializeTrimesterPermissions() {
      try {
        this.permissionsLoading = true;

        // Initialize default structure for all trimesters
        this.trimesterPermissions = {
          1: {
            enabled: false,
            teacherMode: "all",
            selectedTeachers: [],
          },
          2: {
            enabled: false,
            teacherMode: "all",
            selectedTeachers: [],
          },
          3: {
            enabled: false,
            teacherMode: "all",
            selectedTeachers: [],
          },
        };

        const { data } = await apiClient.get(
          `/bulletin/v2/teacher-grade-access/${this.type}`
        );

        if (data && Array.isArray(data)) {
          // Group permissions by trimester
          const permissionsByTrimester = {};

          // First, identify which trimesters have permissions (enabled)
          data.forEach((permission) => {
            const trimester = permission.trimester.toString();

            // If this is the first permission for this trimester, initialize the group
            if (!permissionsByTrimester[trimester]) {
              permissionsByTrimester[trimester] = [];
            }

            // Add this permission to the appropriate trimester group
            permissionsByTrimester[trimester].push(permission);
          });

          // Now process each trimester's permissions
          Object.keys(permissionsByTrimester).forEach((trimester) => {
            const permissions = permissionsByTrimester[trimester];

            // If we have any permissions for this trimester, it means grade editing is enabled
            if (permissions.length > 0) {
              this.trimesterPermissions[trimester].enabled = true;

              // Check if there's a permission without a teacher (means "all teachers")
              const hasAllTeachersPermission = permissions.some(
                (p) => !p.teacher
              );

              if (hasAllTeachersPermission) {
                this.trimesterPermissions[trimester].teacherMode = "all";
              } else {
                // If all permissions have specific teachers, set mode to "specific"
                this.trimesterPermissions[trimester].teacherMode = "specific";

                // Collect all teacher IDs that have permission
                this.trimesterPermissions[trimester].selectedTeachers =
                  permissions
                    .filter((p) => p.teacher) // Only include permissions with a teacher
                    .map((p) => p.teacher); // Extract the teacher ID
              }
            }
          });
        }
      } catch (error) {
        this.$message.error(this.$t("error.erreur"));
      } finally {
        this.permissionsLoading = false;
      }
    },

    async fetchTeachers() {
      const trim = this.selectedTrimester;
      try {
        this.tableLoading = true;
        const { data } = await apiClient.get(
          `/bulletin/v2/teacher-grade-access/teachers/${this.type}/${trim}`
        );

        this.allTeachers = data;
        this.applyFilters();
      } catch (error) {
        this.$message.error(this.$t("error.erreur"));
      } finally {
        this.tableLoading = false;
      }
    },

    applyFilters() {
      let filteredTeachers = [...this.allTeachers];

      // Filter by search text (teacher name)
      if (this.searchText && this.searchText.trim() !== "") {
        const searchLower = this.searchText.toLowerCase();
        filteredTeachers = filteredTeachers.filter((teacher) =>
          teacher.fullName.toLowerCase().includes(searchLower)
        );
      }

      // Filter by selected class
      if (this.selectedClass) {
        filteredTeachers = filteredTeachers.filter((teacher) =>
          teacher.access.some(
            (access) => access.classeRoom._id === this.selectedClass
          )
        );
      }

      // Filter by selected status (enabled/disabled)
      if (this.selectedStatus) {
        filteredTeachers = filteredTeachers.filter((teacher) => {
          const hasPermission = this.checkSpecificTrimesterPermission(
            teacher._id,
            this.selectedTrimester
          );
          return (
            (this.selectedStatus === "enabled" && hasPermission) ||
            (this.selectedStatus === "disabled" && !hasPermission)
          );
        });
      }

      this.teachers = filteredTeachers;
    },

    async getClasses() {
      await apiClient
        .post("/classrooms/filter", {
          query: {
            status: "active",
          },
        })
        .then((res) => (this.classes = res.data));
    },

    async handleTrimesterChange() {
      // Reset filters when trimester changes
      this.searchText = "";
      this.selectedClass = undefined;
      this.selectedStatus = undefined;
      // Refresh teachers and classes data when trimester changes
      await this.fetchTeachers();
      await this.getClasses();
    },

    toggleTrimesterPermission(trimester, enabled) {
      this.trimesterPermissions[trimester].enabled = enabled;
    },

    // Toggle permission for a specific teacher in a specific trimester
    toggleSpecificTrimesterPermission(record, trimester, checked) {
      // Update the trimester permissions in the data model
      const trimPerm = this.trimesterPermissions[trimester];

      if (trimPerm.enabled && trimPerm.teacherMode === "specific") {
        // Ensure we're only modifying the specific teacher's permission
        if (checked && !trimPerm.selectedTeachers.includes(record._id)) {
          // Add this specific teacher to the selected teachers array
          trimPerm.selectedTeachers.push(record._id);
        } else if (!checked) {
          // Remove only this specific teacher from the selected teachers array
          trimPerm.selectedTeachers = trimPerm.selectedTeachers.filter(
            (id) => id !== record._id
          );
        }
      }
    },

    // Check if a teacher has permission for a specific trimester
    checkSpecificTrimesterPermission(teacherId, trimester) {
      const trimPerm = this.trimesterPermissions[trimester];
      if (trimPerm.enabled) {
        if (trimPerm.teacherMode === "all") {
          return true;
        } else if (
          trimPerm.teacherMode === "specific" &&
          trimPerm.selectedTeachers.includes(teacherId)
        ) {
          return true;
        }
      }
      return false;
    },

    // Get unique subjects from all access entries for a teacher
    getUniqueSubjects(record) {
      const subjectsMap = new Map();
      
      if (record.access && Array.isArray(record.access)) {
        record.access.forEach((accessEntry) => {
          if (accessEntry.subjects && Array.isArray(accessEntry.subjects)) {
            accessEntry.subjects.forEach((subject) => {
              if (subject && subject._id) {
                const subjectId = subject._id.toString();
                if (!subjectsMap.has(subjectId)) {
                  subjectsMap.set(subjectId, subject);
                }
              }
            });
          }
        });
      }
      
      return Array.from(subjectsMap.values());
    },

    async savePermissions() {
      this.savingPermissions = true;
      try {
        const trimPerm = this.trimesterPermissions[this.selectedTrimester];
        const numericTrimester = parseInt(this.selectedTrimester);

        // Add validation for specific teachers mode
        if (
          trimPerm.enabled &&
          trimPerm.teacherMode === "specific" &&
          trimPerm.selectedTeachers.length === 0
        ) {
          this.$message.error(
            this.$t("teacherGradePermissions.selectTeachersError")
          );
          return;
        }

        // Save the permission for this trimester
        await apiClient.put("/bulletin/v2/teacher-grade-access", {
          type: this.type,
          trimester: numericTrimester,
          enabled: trimPerm.enabled,
          ...(trimPerm.enabled ? { teacherMode: trimPerm.teacherMode } : {}),
          ...(trimPerm.enabled && trimPerm.teacherMode === "specific"
            ? { selectedTeachers: trimPerm.selectedTeachers }
            : {}),
        });

        this.$message.success(
          this.$t("teacherGradePermissions.permissionSaved")
        );

        // Refresh the data
        await this.initializeTrimesterPermissions();
      } catch (error) {
        this.$message.error(this.$t("error.erreur"));
      } finally {
        this.savingPermissions = false;
      }
    },
  },
};
</script>

<style scoped>
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

.subjects-container {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
</style>
